import { defineStore, DefineStoreOptions } from 'pinia';
import { useMediaQuery } from '@vueuse/core';
import {
  BusinessInfoResponse,
  FetchListByGroupCodeResponse,
  FetchSystemInfoResponse,
} from '@dto';
import type { Ref } from 'vue';

// import { ROUTE_NAME } from '@constants';

// interface
interface CommonStoreState {
  totalSystemInfo: FetchSystemInfoResponse | null;
  // isLoading: boolean;
  loadingCount: number;
  isSignIn: boolean;
  // isMenu: boolean;
  accessToken: string | null;
  membershipId: string;
  isMobileSize: Ref<boolean> | boolean;
  isShowScrollTop: boolean;
  areaCodeList: FetchListByGroupCodeResponse | [];
  mainSwiperActiveIndex: number;
  isScrollBeyondThreshold: boolean;
  isHamburgerClicked: boolean;
  routeName: string;
  scrollY: number;
  isSigninOpen: boolean;
  isMembershipChangeFlag: boolean;
  ccType: string;
  businessPlaces: BusinessInfoResponse[];
}
interface CommonStoreGetters {
  isTotalSystemInfo: (
    state: CommonStoreState
  ) => FetchSystemInfoResponse | null;
  getLocalStorage: (
    state: CommonStoreState
  ) => (params: string) => string | null;
  getSessionStorage: (
    state: CommonStoreState
  ) => (params: string) => string | null;
  isDesktop: (state: CommonStoreState) => boolean;
  isMobile: (state: CommonStoreState) => boolean;
  // isLogoCenter: (state: CommonStoreState) => boolean;
  // isMainFirstSwiper: (state: CommonStoreState) => boolean;
  isNotOverScrollY: (state: CommonStoreState) => boolean;
  signinOpen: (state: CommonStoreState) => boolean;
  membershipChangeFlag: (state: CommonStoreState) => boolean;
  bizCodeList: (
    state: CommonStoreState
  ) => { bizCode: string; ccName: string }[];
  BIZ_CODES: (state: CommonStoreState) => Record<string, string>;
  isLoading: (state: CommonStoreState) => boolean;
}
interface CommonStoreActions {
  setTotalSystemInfo: (info: FetchSystemInfoResponse) => void;
  setIsLoading: (loadingState: boolean) => void;
  setIsSignIn: (signInState: boolean) => void;
  setAccessToken: (token: string, membershipId?: string) => void;
  clearAccessToken: () => void;
  setStorage: (repo: string, field: string, value: string) => void;
  removeStorage: (repo: RepoStorage, field: string) => void;
  setAreaCodeList: (areaCodeList: FetchListByGroupCodeResponse) => void;
  setScrollTop: (isShow: boolean) => void;
  setMainSwiperActiveIndex: (swiperIndex: number) => void;
  setIsScrollBeyondThreshold: (isScrollBeyondThreshold: boolean) => void;
  setRouteName: (routeName: string) => void;
  setGnbOpen: () => void;
  setGnbClose: () => void;
  setScrollY: (scrollY: number) => void;
  setSignIn: (signInState: boolean) => void;
  setSigninOpen: (isOpen: boolean) => void;
  setMembershipChange: (isOpen: boolean) => void;
  setCcType: (ccType: string) => void;
  setBusinessPlaces: (businessPlaces: BusinessInfoResponse[]) => void;
}

type CommonStoreOptions = DefineStoreOptions<
  string,
  CommonStoreState,
  CommonStoreGetters,
  CommonStoreActions
>;

type RepoStorage = 'sessionStorage' | 'localStorage';

const isSignInFromSessionStorage = sessionStorage.getItem('login');
const commonStoreId = 'common';
const commonStoreOptions: CommonStoreOptions = {
  id: commonStoreId,
  state: () => {
    return {
      totalSystemInfo: null,
      // isLoading: false,
      loadingCount: 0,
      isMobileSize: useMediaQuery('(max-width: 884px)') as Ref<boolean>,
      isSignIn: isSignInFromSessionStorage
        ? JSON.parse(isSignInFromSessionStorage)
        : false,
      accessToken: null,
      membershipId: '',
      isShowScrollTop: false,
      areaCodeList: [],
      mainSwiperActiveIndex: 0,
      isScrollBeyondThreshold: false,
      isHamburgerClicked: false,
      routeName: '',
      scrollY: 0,
      isSigninOpen: false,
      isMembershipChangeFlag: false,
      ccType: 'yc',
      businessPlaces: [],
    };
  },
  getters: {
    signinOpen(state) {
      return state.isSigninOpen;
    },
    membershipChangeFlag(state) {
      return state.isMembershipChangeFlag;
    },
    isTotalSystemInfo() {
      return this.totalSystemInfo;
    },
    getLocalStorage: () => (params: string) => {
      return localStorage.getItem(params);
    },
    getSessionStorage: () => (params: string) => {
      return sessionStorage.getItem(params);
    },
    isDesktop: (state: CommonStoreState) => !state.isMobileSize as boolean,
    isMobile: (state: CommonStoreState) => state.isMobileSize as boolean,
    isMainFirstSwiper(state) {
      return state.mainSwiperActiveIndex === 0;
    },
    isNotOverScrollY(state) {
      return state.scrollY < 200;
    },
    bizCodeList(state) {
      return state.businessPlaces.map((biz) => ({
        bizCode: biz.bizCode,
        ccName: biz.ccName,
      }));
    },
    BIZ_CODES(state) {
      // enum 처럼 사용하기 위해 만든 것
      return state.businessPlaces.reduce(
        (acc, biz) => ({
          ...acc,
          [biz.ccName.includes('영천')
            ? 'YC'
            : biz.ccName.includes('포항')
              ? 'PH'
              : biz.ccName.includes('청도')
                ? 'CD'
                : biz.bizCode]: biz.bizCode,
        }),
        {} as Record<string, string>
      );
    },
    isLoading(state) {
      return state.loadingCount > 0;
    },
  },
  actions: {
    setTotalSystemInfo(info: FetchSystemInfoResponse) {
      this.totalSystemInfo = info;
    },
    setIsLoading(loadingState: boolean) {
      if (loadingState) {
        this.loadingCount++;
      } else {
        this.loadingCount = Math.max(this.loadingCount - 1, 0);
      }
    },
    setIsSignIn(signInState: boolean) {
      this.isSignIn = signInState;
    },
    setAccessToken(token: string, membershipId?: string) {
      this.accessToken = token;
      this.setIsSignIn(true);
      this.setStorage('sessionStorage', 'token', token);
      this.setStorage('sessionStorage', 'login', String(true));
      if (membershipId) {
        this.setStorage('sessionStorage', 'membershipId', membershipId);
      }
    },
    clearAccessToken() {
      this.accessToken = null;
      this.setIsSignIn(false);
      this.removeStorage('sessionStorage', 'membershipId');
      this.removeStorage('sessionStorage', 'token');
      this.setStorage('sessionStorage', 'login', String(false));
    },
    setStorage(repo: string, field: string, value: string) {
      repo === 'sessionStorage'
        ? sessionStorage.setItem(field, value)
        : localStorage.setItem(field, value);
    },
    removeStorage(repo: RepoStorage, field: string) {
      repo === 'sessionStorage'
        ? sessionStorage.removeItem(field)
        : localStorage.removeItem(field);
    },
    setAreaCodeList(areaCodeList: FetchListByGroupCodeResponse) {
      this.areaCodeList = areaCodeList;
    },
    setScrollTop(isShow: boolean) {
      this.isShowScrollTop = isShow;
    },
    setMainSwiperActiveIndex(swiperIndex: number): void {
      this.mainSwiperActiveIndex = swiperIndex;
    },
    setIsScrollBeyondThreshold(isScrollBeyondThreshold: boolean): void {
      this.isScrollBeyondThreshold = isScrollBeyondThreshold;
    },
    setRouteName(routeName: string): void {
      this.routeName = routeName;
    },
    setGnbOpen(): void {
      this.isHamburgerClicked = true;
    },
    setGnbClose(): void {
      this.isHamburgerClicked = false;
    },
    setScrollY(scrollY: number): void {
      this.scrollY = scrollY;
    },
    setSignIn(signInState: boolean) {
      this.isSignIn = signInState;
    },
    setSigninOpen(isOpen: boolean) {
      this.isSigninOpen = isOpen;
    },
    setMembershipChange(isOpen: boolean) {
      this.isMembershipChangeFlag = isOpen;
    },
    setCcType(ccType: string) {
      this.ccType = ccType;
    },
    setBusinessPlaces(businessPlaces: BusinessInfoResponse[]) {
      this.businessPlaces = businessPlaces;
    },
  },
};

export const useCommonStore = defineStore(commonStoreId, commonStoreOptions);
